import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, MessageCircle, Facebook, Instagram, Twitter, Youtube, Linkedin, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platformIcons = {
  facebook: Facebook, instagram: Instagram, twitter: Twitter,
  youtube: Youtube, linkedin: Linkedin, whatsapp: MessageCircle, website: Globe,
};

// Footer-specific hospital name renderer.
// We deliberately FORCE white on navy (and mint-teal accent for parenthetical words)
// regardless of admin-saved per-word colors — those colors are chosen for the light
// navbar and would be invisible on the dark navy footer. Admins keep full control
// over the navbar's color customization; the footer is just always legible.
const FooterHospitalName = ({ settings, language, fontSize: overrideFontSize }) => {
  const name = language === 'en' ? settings?.hospital_name_en : settings?.hospital_name_hi;
  const hospitalSuffix = language === 'en' ? '(Hospital)' : '(अस्पताल)';
  const allWords = (name || '').split(' ').filter((w) => w);
  const hasHospitalSuffix = allWords.some((w) => w.includes('('));
  const words = hasHospitalSuffix ? allWords : [...allWords, hospitalSuffix];
  const wordStyles = settings?.hospital_name_word_styles || [];
  const baseFontSize = overrideFontSize || settings?.footer_font_size || 18;

  if (words.length === 0) {
    return (
      <span className="font-heading font-light uppercase tracking-wider text-white" style={{ fontSize: `${baseFontSize}px` }}>
        Hospital
      </span>
    );
  }

  return (
    <span className="leading-tight flex flex-wrap gap-x-1.5 items-baseline">
      {words.map((word, index) => {
        const isParenthetical = word.includes('(') || word.includes(')');
        const savedStyle =
          wordStyles.find((s) => s.word === word && s.index === index) ||
          wordStyles.find((s) => s.index === index) ||
          {};
        const wordFontSize = isParenthetical ? Math.max(baseFontSize - 3, 11) : baseFontSize;
        return (
          <span
            key={`${word}-${index}`}
            style={{
              // Force white/teal-tint on navy — ignore saved color
              color: isParenthetical ? '#5EEAD4' : '#FFFFFF',
              fontFamily: savedStyle.font || 'Playfair Display, serif',
              fontWeight: savedStyle.fontWeight || (isParenthetical ? '300' : '400'),
              fontSize: `${wordFontSize}px`,
              letterSpacing: '0.08em',
            }}
          >
            {word}
          </span>
        );
      })}
    </span>
  );
};

const Footer = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [contactNumbers, setContactNumbers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, socialRes, numbersRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/social-media`),
          axios.get(`${API}/contact-numbers`),
        ]);
        setSettings(settingsRes.data);
        setSocialLinks(socialRes.data);
        setContactNumbers(numbersRes.data);
      } catch (error) {
        console.error('Failed to fetch footer data:', error);
      }
    };
    fetchData();
  }, []);

  const quickLinks = [
    { path: '/', label_en: 'Home', label_hi: 'होम' },
    { path: '/services', label_en: 'Services', label_hi: 'सेवाएँ' },
    { path: '/doctors', label_en: 'Doctors', label_hi: 'डॉक्टर' },
    { path: '/jobs', label_en: 'Careers', label_hi: 'करियर' },
    { path: '/contact', label_en: 'Contact', label_hi: 'संपर्क' },
  ];

  return (
    <footer className="bg-brand-navy text-white" data-testid="footer">
      <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              {settings?.logo_url && (
                <div className="bg-white rounded-full p-1.5 shadow-sm flex-shrink-0">
                  <img
                    src={settings.logo_url}
                    alt="Logo"
                    className="h-10 w-10 object-contain rounded-full"
                  />
                </div>
              )}
              <FooterHospitalName settings={settings} language={language} />
            </div>
            <p className="text-white/75 text-sm font-body font-light leading-relaxed mb-6">
              {language === 'en'
                ? 'Providing quality healthcare services with compassion and excellence.'
                : 'करुणा और उत्कृष्टता के साथ गुणवत्तापूर्ण स्वास्थ्य सेवाएँ।'}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex gap-3">
                {socialLinks.map((link) => {
                  const Icon = platformIcons[link.platform] || Globe;
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 border border-white/30 hover:border-white hover:bg-white/10 transition-colors rounded"
                      data-testid={`social-${link.platform}`}
                      aria-label={link.platform}
                    >
                      <Icon className="h-4 w-4 text-white/80 hover:text-white" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs uppercase tracking-ultra-wide font-body mb-5 text-brand-teal-light font-medium">
              {language === 'en' ? 'Quick Links' : 'त्वरित लिंक'}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-white transition-colors text-sm font-body font-light"
                    data-testid={`footer-link-${link.path.substring(1) || 'home'}`}
                  >
                    {language === 'en' ? link.label_en : link.label_hi}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Numbers */}
          <div>
            <h3 className="text-xs uppercase tracking-ultra-wide font-body mb-5 text-brand-teal-light font-medium">
              {language === 'en' ? 'Contact' : 'संपर्क'}
            </h3>
            <ul className="space-y-3">
              {contactNumbers.length > 0 ? (
                contactNumbers.slice(0, 4).map((num) => (
                  <li key={num.id} className="flex items-start gap-2">
                    {num.is_whatsapp ? (
                      <MessageCircle className="h-3.5 w-3.5 text-brand-teal-light flex-shrink-0 mt-1" />
                    ) : (
                      <Phone className="h-3.5 w-3.5 text-brand-teal-light flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <a
                        href={`tel:${num.number}`}
                        className="block text-white/90 hover:text-white transition-colors text-sm font-body font-light"
                      >
                        {num.number}
                      </a>
                      <p className="text-xs text-white/60 font-body">
                        {language === 'en' ? num.label_en : num.label_hi}
                      </p>
                    </div>
                  </li>
                ))
              ) : (
                <li className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 text-brand-teal-light" />
                  <a
                    href={`tel:${settings?.phone}`}
                    className="text-white/90 hover:text-white text-sm font-body font-light"
                  >
                    {settings?.phone}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-xs uppercase tracking-ultra-wide font-body mb-5 text-brand-teal-light font-medium">
              {language === 'en' ? 'Address' : 'पता'}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-brand-teal-light flex-shrink-0 mt-0.5" />
                <span className="text-white/80 text-sm font-body font-light leading-relaxed">
                  {language === 'en' ? settings?.address_en : settings?.address_hi}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand-teal-light flex-shrink-0" />
                <a
                  href={`mailto:${settings?.email}`}
                  className="text-white/85 hover:text-white transition-colors text-sm font-body font-light break-all"
                >
                  {settings?.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/15 mt-12 sm:mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/65 font-body font-light flex items-center gap-1.5 flex-wrap justify-center">
            <span>&copy; {new Date().getFullYear()}</span>
            <FooterHospitalName settings={settings} language={language} fontSize={12} />
          </p>
          <Link to="/admin/login" className="text-xs text-white/40 hover:text-white/80 transition-colors font-body">
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
